import type { ReactElement } from 'react';

import type { Screen } from './screens';

/**
 * The menu's icons (C4a correction, 2026-10-18).
 *
 * The operator asked for icons beside the labels — *"Use icons as well"* — and the labels stay,
 * because an icon alone is a guess at 360 px and a label is not. Nothing here is drawn to be
 * self-explanatory: each icon repeats the word next to it.
 *
 * **Hand-drawn rather than installed.** The task this comes from rules out a new dependency for four
 * icons, and `apps/web/package.json` carries no icon set to reuse — `@radix-ui/react-dialog` and
 * `@radix-ui/react-select` are the only UI packages, and neither ships glyphs. These are the paths
 * themselves: no package, no build step, no sprite to fetch.
 *
 * **Nothing here is the only label a screen reader hears.** Every glyph is `aria-hidden="true"` and
 * `focusable="false"`, so the accessible name of each control is its own text label and nothing else.
 * The `<title>`-less, `role`-less markup is deliberate for the same reason.
 *
 * Every icon is `currentColor` on a 24-unit grid, so it inherits the colour of the control it sits in
 * — including the `aria-current="page"` item — and is sized by the stylesheet rather than pinned to
 * pixels here.
 */

/** The attributes every icon shares. */
const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
} as const;

/** The ledger: a book of accounts with its entry lines. */
export function LedgerIcon(): ReactElement {
  return (
    <svg className="icon" {...ICON_PROPS}>
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M8 8.5h8" />
      <path d="M8 12.5h8" />
      <path d="M8 16.5h5" />
    </svg>
  );
}

/** Accounts: the wallet the accounts live in. */
export function AccountsIcon(): ReactElement {
  return (
    <svg className="icon" {...ICON_PROPS}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 11h18" />
      <path d="M16 15h2" />
    </svg>
  );
}

/** Recurring: the schedule that comes round again. */
export function RecurringIcon(): ReactElement {
  return (
    <svg className="icon" {...ICON_PROPS}>
      <path d="M17 2.5l3.5 3.5L17 9.5" />
      <path d="M3.5 11.5v-1a4 4 0 0 1 4-4h13" />
      <path d="M7 21.5L3.5 18 7 14.5" />
      <path d="M20.5 12.5v1a4 4 0 0 1-4 4h-13" />
    </svg>
  );
}

/** Reports: the figures as columns. */
export function ReportsIcon(): ReactElement {
  return (
    <svg className="icon" {...ICON_PROPS}>
      <path d="M6 20v-6" />
      <path d="M12 20V4" />
      <path d="M18 20v-9" />
      <path d="M3.5 20h17" />
    </svg>
  );
}

/** Sign out: leaving through the door — never a glyph that reads as destructive. */
export function SignOutIcon(): ReactElement {
  return (
    <svg className="icon" {...ICON_PROPS}>
      <path d="M10 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M17 16.5l4.5-4.5L17 7.5" />
      <path d="M21.5 12H10" />
    </svg>
  );
}

/** The mark beside the wordmark: the app's own tile, drawn at the bar's own size. */
export function BrandMark(): ReactElement {
  return (
    <svg className="icon brand-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="1.5" y="1.5" width="21" height="21" rx="5" fill="currentColor" stroke="none" />
      {[8, 12, 16].map((y) =>
        [8, 12, 16].map((x) => (
          // The dots are painted by the stylesheet (`fill: var(--rail)` on `.brand-glyph circle`)
          // rather than by an attribute here: a token belongs in one place, and this is a colour the
          // bar's own background supplies.
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1.1" stroke="none" />
        )),
      )}
    </svg>
  );
}

/**
 * The icon for a screen, by the screen's own name.
 *
 * Keyed by `Screen` — not by index or by a second list — so a screen added to the registry in
 * `screens.ts` without an icon is a type error rather than a bar that renders a label with a gap in
 * front of it.
 */
export const SCREEN_ICONS: Readonly<Record<Screen, () => ReactElement>> = {
  ledger: LedgerIcon,
  accounts: AccountsIcon,
  recurring: RecurringIcon,
  reports: ReportsIcon,
};
